import { useEffect, useRef, useState } from "react";
import { Package, Pencil, Trash2, GripVertical, BellRing, RotateCcw, Send } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Input } from "@/components/ui/input";
import { photoThumb, photoThumbBackend } from "@/lib/photoUrl";
import { useColumnLayout } from "@/hooks/useColumnLayout";
import AutoGrowTextarea from "@/components/quotes/AutoGrowTextarea";
import ProcurementFilingDialog from "@/components/quotes/ProcurementFilingDialog";
import { FloatingHScrollbar } from "@/components/FloatingHScrollbar";

const cellInput = "h-7 px-1.5 text-xs rounded-sm border-transparent bg-transparent hover:border-border focus:border-brand focus:bg-white transition-colors";

// Default widths (px) for the 报价单产品明细 table; users can drag any header
// edge and the choice is persisted per browser.
const DEFAULT_COL_WIDTHS = {
  select: 36, photo: 170, description: 250, material: 250, size: 170, calc_price: 115,
  price: 130, qty_per_ctn: 90, factory_cost: 120, qty40: 95, actions: 44,
};

// 2026-06 · Columns the user may reorder by dragging their header. ``select``
// stays pinned first and ``actions`` last. Order is persisted per browser.
const ORDERABLE_COLS = ["photo", "description", "material", "size", "factory_cost",
                        "calc_price", "price", "qty_per_ctn", "qty40"];
const ORDER_STORAGE_KEY = "quotesys_quote_col_order_v1";

// 2026-06 · 采购已填报 badge lifetime: it stays until 2 days after the 外销
// first opened the quote (``procurement_filled_seen_at``, stamped by the
// backend on load). Never stamped yet → always show.
const FILLED_BADGE_TTL_MS = 2 * 24 * 60 * 60 * 1000;
const showFilledBadge = (item) => {
  const seen = item.procurement_filled_seen_at;
  if (!seen) return true;
  const ts = Date.parse(seen);
  if (Number.isNaN(ts)) return true;
  return Date.now() - ts < FILLED_BADGE_TTL_MS;
};

// 2026-06 · 红色「已发给采购询价」only stays until that 采购 actually files a
// quote for the product. If a NEWER link is generated after the last filing
// (re-enquiry) it shows again.
const showLinkSentBadge = (item) => {
  const sent = item.procurement_link_sent_at;
  if (!sent) return false;
  if (!(item.procurement_filled_count > 0)) return true;
  const filledAt = item.procurement_filled_at;
  if (!filledAt) return false;
  return Date.parse(sent) > Date.parse(filledAt);
};

const readStoredOrder = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(ORDER_STORAGE_KEY) || "null");
    if (!Array.isArray(saved)) return ORDERABLE_COLS;
    const kept = saved.filter((k) => ORDERABLE_COLS.includes(k));
    return [...kept, ...ORDERABLE_COLS.filter((k) => !kept.includes(k))];
  } catch {
    return ORDERABLE_COLS;
  }
};

/**
 * 报价单产品明细表格 — photo, description, material and one row per size with
 * inline editing, drag-to-sort, per-column resize, drag-to-reorder columns and
 * the 采购已填报 marker. Extracted from pages/Quotes.jsx (2026-06 refactor).
 */
export default function QuoteItemsTable({ fullscreen = false,
  detail, readOnly, detailReadOnly,
  onOpenEditor, removeItem, removeRow,
  setItemField, saveItemField, setRowField, saveRowField,
  toggleAllRowsSelected, handleQuotePhotoReplace, resetQuotePhoto,
  startRowDrag, dragRow, overRow, fmtRowCost,
  startItemDrag, dragItem, overItem,
}) {
  const { t } = useLanguage();
  const { widths: colWidths, startResize } = useColumnLayout("quotesys_quote_widths_v3", DEFAULT_COL_WIDTHS);

  const [colOrder, setColOrder] = useState(readStoredOrder);
  const [dragCol, setDragCol] = useState(null);
  const [overCol, setOverCol] = useState(null);
  // 2026-06 · 采购填报速览 — opened by clicking the green badge.
  const [filingItem, setFilingItem] = useState(null);

  const saveOrder = (next) => {
    setColOrder(next);
    try { localStorage.setItem(ORDER_STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  };

  const dropOnCol = (target) => {
    if (!dragCol || dragCol === target) { setDragCol(null); setOverCol(null); return; }
    const next = colOrder.filter((k) => k !== dragCol);
    next.splice(next.indexOf(target), 0, dragCol);
    saveOrder(next);
    setDragCol(null);
    setOverCol(null);
  };

  const dataCols = colOrder.filter((k) => k !== "factory_cost" || !readOnly);
  const visibleCols = ["select", ...dataCols, "actions"];
  const tableWidth = visibleCols.reduce((sum, k) => sum + (colWidths[k] || 60), 0);

  // 2026-06 · 冻结首列 —— 勾选框 / 图片 / 产品描述 固定在左侧，横向滚动看价格时
  // 仍然知道在看哪个产品。只冻结从最左开始连续的这几列（用户拖动列序后自动失效）。
  const FREEZE_KEYS = ["select", "photo", "description"];
  const frozenLeft = {};
  {
    let left = 0;
    for (const key of visibleCols) {
      if (!FREEZE_KEYS.includes(key)) break;
      frozenLeft[key] = left;
      left += colWidths[key] || 60;
    }
  }
  const frozenKeys = Object.keys(frozenLeft);
  const lastFrozen = frozenKeys[frozenKeys.length - 1];
  const freezeStyle = (key, z = 12) => (key in frozenLeft
    ? { position: "sticky", left: frozenLeft[key], zIndex: z }
    : undefined);
  const freezeClass = (key, bg = "bg-card") => (key in frozenLeft
    ? `${bg} ${key === lastFrozen ? "shadow-[1px_0_0_0_hsl(var(--border))]" : ""}`
    : "");

  const LABELS = {
    photo: t("quote.photo"),
    description: t("quote.description"),
    material: t("products.material"),
    size: t("quote.size"),
    factory_cost: t("priceList.factoryCost"),
    calc_price: `${t("quote.calcPrice")} (${detail.currency})`,
    price: `${t("priceList.price")} (${detail.currency === "CAD" ? "C$" : "US$"})`,
    qty_per_ctn: t("priceList.qtyPerCtn"),
    qty40: t("quote.qty"),
  };
  // 2026-06 · 让表格高度止于视口底部 —— 横向滚动条永远可见（不用滚到最后一条
  // 产品才能拖动），表头同时吸顶。
  const scrollBoxRef = useRef(null);
  const [boxMaxH, setBoxMaxH] = useState(0);
  useEffect(() => {
    const calc = () => {
      const el = scrollBoxRef.current;
      if (!el) return;
      setBoxMaxH(Math.max(360, window.innerHeight - el.getBoundingClientRect().top - 24));
    };
    calc();
    // recalc after the layout settles (e.g. entering/leaving 全屏)
    const tid = setTimeout(calc, 120);
    window.addEventListener("resize", calc);
    return () => { clearTimeout(tid); window.removeEventListener("resize", calc); };
  }, [fullscreen]);

  const ALIGN = {    photo: "text-left", description: "text-left", material: "text-left", size: "text-left",
    factory_cost: "text-right", calc_price: "text-right", price: "text-right",
    qty_per_ctn: "text-right", qty40: "text-right",
  };
  // Product-level cells span every price row of the product.
  const ITEM_LEVEL = ["photo", "description", "material"];

  return (
    <>
    <div
      ref={scrollBoxRef}
      style={{ maxHeight: boxMaxH ? `${boxMaxH}px` : undefined }}
      className="bg-card border border-border rounded-md overflow-auto"
      data-testid="quote-items-scroll">
      <table className="text-xs [&_tbody>tr>td]:relative [&_tbody>tr>td]:z-0 [&_tbody>tr>td]:border [&_tbody>tr>td]:border-[#7f7f7f]" style={{ tableLayout: "fixed", borderCollapse: "collapse", width: tableWidth }} data-testid="quote-items-table">
        <colgroup>
          {visibleCols.map((key) => (
            <col key={key} style={{ width: colWidths[key] }} />
          ))}
        </colgroup>
        <thead>
          <tr className="bg-[#d9d9d9] dark:bg-secondary">
            <th
              style={freezeStyle("select", 30)}
              className={`sticky top-0 z-20 bg-[#d9d9d9] dark:bg-secondary border border-[#7f7f7f] relative text-center text-[10px] font-bold text-foreground px-1 py-1 select-none ${freezeClass("select", "bg-[#d9d9d9] dark:bg-secondary")}`}
            >
              <input
                type="checkbox"
                disabled={readOnly}
                checked={(() => {
                  const all = (detail.items || []).flatMap((it) => it.price_rows || []);
                  return all.length > 0 && all.every((r) => r.selected !== false);
                })()}
                ref={(el) => {
                  if (!el) return;
                  const all = (detail.items || []).flatMap((it) => it.price_rows || []);
                  const some = all.some((r) => r.selected !== false);
                  const every = all.length > 0 && all.every((r) => r.selected !== false);
                  el.indeterminate = some && !every;
                }}
                onChange={(e) => toggleAllRowsSelected(e.target.checked)}
                data-testid="quote-select-all-rows"
                title={t("quote.selectAllRows")}
                className="cursor-pointer"
              />
              <span
                onMouseDown={startResize("select", 34)}
                data-testid="quote-col-resize-select"
                className="absolute top-0 -right-1 z-10 h-full w-2.5 cursor-col-resize hover:bg-brand/50"
              />
            </th>
            {dataCols.map((key) => (
              <th
                key={key}
                draggable
                onDragStart={(e) => { setDragCol(key); e.dataTransfer.effectAllowed = "move"; }}
                onDragOver={(e) => { e.preventDefault(); setOverCol(key); }}
                onDragLeave={() => setOverCol((c) => (c === key ? null : c))}
                onDrop={(e) => { e.preventDefault(); dropOnCol(key); }}
                onDragEnd={() => { setDragCol(null); setOverCol(null); }}
                title={t("quote.colDragHint")}
                data-testid={`quote-col-head-${key}`}
                style={freezeStyle(key, 30)}
                className={`sticky top-0 z-20 bg-[#d9d9d9] dark:bg-secondary border border-[#7f7f7f] relative text-center text-[10px] font-bold text-foreground px-1 py-1 select-none cursor-grab active:cursor-grabbing transition-colors ${freezeClass(key, "bg-[#d9d9d9] dark:bg-secondary")} ${dragCol === key ? "opacity-40" : ""} ${overCol === key && dragCol && dragCol !== key ? "bg-brand/15" : ""}`}
              >
                <span className="block truncate">{LABELS[key]}</span>
                <span
                  draggable={false}
                  onDragStart={(e) => e.preventDefault()}
                  onMouseDown={(e) => { e.stopPropagation(); startResize(key, 34)(e); }}
                  data-testid={`quote-col-resize-${key}`}
                  className="absolute top-0 -right-1 z-10 h-full w-2.5 cursor-col-resize hover:bg-brand/50"
                />
              </th>
            ))}
            <th className="text-center px-1 py-1 border border-[#7f7f7f] bg-[#d9d9d9] dark:bg-secondary">
              <button
                type="button"
                onClick={() => saveOrder(ORDERABLE_COLS)}
                title={t("quote.colOrderReset")}
                data-testid="quote-col-order-reset"
                className="p-1 rounded text-muted-foreground/60 hover:text-brand hover:bg-brand/10 transition-colors"
              >
                <RotateCcw size={12} />
              </button>
            </th>
          </tr>
        </thead>
        <tbody>
          {detail.items.map((p, pIdx) => {
            const rows = p.price_rows?.length ? p.price_rows : [null];
            const span = rows.length;
            return rows.map((r, ri) => {
              // Product-level cells — rendered only on the product's first row.
              const itemCells = {
                photo: (
                  <td key="photo" rowSpan={span}
                    style={{ padding: "2px", ...(freezeStyle("photo") || {}) }}
                    className={`align-middle border-r border-border/40 ${freezeClass("photo")}`}>
                    <div className="relative w-full group">
                      {/* 2026-06 · 有订单徽章 */}
                      {p.has_order && (
                        <span
                          className="absolute top-0.5 right-0.5 z-10 px-1 py-0.5 rounded-sm bg-emerald-500 text-white text-[9px] font-bold tracking-wide shadow-sm"
                          data-testid={`item-order-badge-${p.item_number}`}
                        >
                          {t("quote.hasOrderBadge")}
                        </span>
                      )}
                      {(p.quote_photo_url || p.composite_photo_url || p.photo_url) ? (                        (() => {
                          const src = p.quote_photo_url || p.composite_photo_url || p.photo_url;
                          return (
                            <img
                              src={photoThumb(src, "md")}
                              alt={p.item_number}
                              className="w-full rounded-sm object-contain bg-white"
                              onError={(e) => {
                                const i = e.currentTarget;
                                if (i.dataset.f !== "1") { i.dataset.f = "1"; i.src = photoThumbBackend(src, "md"); }
                              }}
                            />
                          );
                        })()
                      ) : (
                        <div className="w-full aspect-square rounded-sm bg-secondary flex items-center justify-center">
                          <Package size={28} className="text-muted-foreground" />
                        </div>
                      )}
                      {!detailReadOnly && (
                        <div className="absolute inset-x-0 bottom-0 opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 text-white text-[10px] px-1.5 py-1 flex items-center justify-between gap-1">
                          <label className="cursor-pointer flex items-center gap-1 hover:underline" data-testid={`item-photo-replace-${p.item_number}`}>
                            <input type="file" accept="image/*" className="hidden" onChange={(e) => handleQuotePhotoReplace(p.item_number, e.target.files?.[0])} />
                            {t("quote.replacePhoto")}
                          </label>
                          {p.quote_photo_url && (
                            <button type="button" onClick={() => resetQuotePhoto(p.item_number)} className="hover:underline" data-testid={`item-photo-reset-${p.item_number}`}>
                              {t("quote.resetPhoto")}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                ),
                description: (
                  <td key="description" rowSpan={span}
                    style={freezeStyle("description")}
                    className={`px-2 py-1 align-top border-r border-border/40 ${freezeClass("description")}`}>
                    <div className="flex items-start gap-1">
                      {!detailReadOnly && (
                        <span
                          onPointerDown={startItemDrag?.(p.item_number)}
                          title={t("quote.itemDragToSort")}
                          data-testid={`item-drag-${p.item_number}`}
                          className="shrink-0 mt-0.5 cursor-grab active:cursor-grabbing touch-none text-muted-foreground/50 hover:text-brand transition-colors"
                        >
                          <GripVertical size={13} />
                        </span>
                      )}
                      <button
                        type="button"
                        className="font-mono font-bold text-xs mb-0.5 text-brand hover:underline flex items-center gap-1 group/edit"
                        onClick={() => onOpenEditor(pIdx)}
                        data-testid={`item-open-editor-${p.item_number}`}
                        title={t("quote.editor.openHint")}
                      >
                        {p.item_number}
                        <Pencil size={9} className="opacity-40 group-hover/edit:opacity-100 transition" />
                      </button>
                      {showLinkSentBadge(p) && (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-red-500/10 text-red-600 dark:text-red-400 font-semibold shrink-0"
                          title={t("quote.procLinkSentHint").replace("{date}", String(p.procurement_link_sent_at).slice(0, 10))}
                          data-testid={`item-proc-link-sent-${p.item_number}`}
                        >
                          <Send size={9} /> {t("quote.procLinkSent")}
                        </span>
                      )}
                      {p.procurement_filled_count > 0 && showFilledBadge(p) && (
                        <button
                          type="button"
                          onClick={() => setFilingItem(p.item_number)}
                          className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 font-medium shrink-0 hover:bg-emerald-500/25 transition-colors"
                          title={t("quote.filing.openHint")}
                          data-testid={`item-proc-filled-${p.item_number}`}
                        >
                          <BellRing size={9} /> {t("quote.procFilled").replace("{n}", String(p.procurement_filled_count))}
                        </button>
                      )}
                      {!detailReadOnly && (
                        <button
                          type="button"
                          onClick={() => removeItem(p.item_number)}
                          data-testid={`item-remove-${p.item_number}`}
                          title={t("quote.removeItem")}
                          className="ml-auto p-0.5 rounded text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
                        >
                          <Trash2 size={11} />
                        </button>
                      )}
                    </div>
                    <AutoGrowTextarea
                      value={p.description || ""}
                      placeholder={t("products.description")}
                      disabled={detailReadOnly}
                      onChange={(e) => setItemField(p.item_number, "description", e.target.value)}
                      onBlur={(e) => saveItemField(p.item_number, "description", e.target.value)}
                      data-testid={`item-desc-${p.item_number}`}
                    />
                    {/* 2026-06 · 是否有订单 + 订单号（放在产品描述下面；仅系统内部，不参与导出） */}
                    <div className="mt-1" data-testid={`item-order-box-${p.item_number}`}>
                      <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground cursor-pointer select-none hover:text-foreground transition-colors">
                        <input
                          type="checkbox"
                          disabled={detailReadOnly}
                          checked={Boolean(p.has_order)}
                          onChange={(e) => {
                            setItemField(p.item_number, "has_order", e.target.checked);
                            saveItemField(p.item_number, "has_order", e.target.checked);
                          }}
                          data-testid={`item-has-order-${p.item_number}`}
                          className="cursor-pointer"
                        />
                        {t("quote.hasOrder")}
                      </label>
                      {p.has_order && (
                        <Input
                          value={p.order_number || ""}
                          disabled={detailReadOnly}
                          onChange={(e) => setItemField(p.item_number, "order_number", e.target.value)}
                          onBlur={(e) => saveItemField(p.item_number, "order_number", e.target.value)}
                          placeholder={t("quote.orderNumber")}
                          className={`${cellInput} mt-1 w-full`}
                          data-testid={`item-order-number-${p.item_number}`}
                        />
                      )}
                    </div>
                  </td>
                ),
                material: (
                  <td key="material" rowSpan={span}
                    className="px-2 py-1 align-top border-r border-border/40">
                    <AutoGrowTextarea
                      value={p.material || ""}
                      placeholder={t("products.material")}
                      disabled={detailReadOnly}
                      onChange={(e) => setItemField(p.item_number, "material", e.target.value)}
                      onBlur={(e) => saveItemField(p.item_number, "material", e.target.value)}
                      data-testid={`item-material-${p.item_number}`}
                    />
                  </td>
                ),
              };

              // Price-row cells — one per size row.
              const rowCells = !r ? {} : {
                size: (
                  <td key="size" className="px-1.5 py-0.5 align-top">
                    <div className="flex items-start gap-1">
                      {!detailReadOnly && (
                        <span
                          onPointerDown={startRowDrag(p.item_number, r.id)}
                          title={t("quote.editor.dragToSort")}
                          data-testid={`row-drag-${p.item_number}-${ri}`}
                          className="shrink-0 mt-1 cursor-grab active:cursor-grabbing touch-none text-muted-foreground/50 hover:text-brand transition-colors"
                        >
                          <GripVertical size={13} />
                        </span>
                      )}
                      <AutoGrowTextarea
                        value={r.size || ""}
                        placeholder={t("quote.size")}
                        disabled={detailReadOnly}
                        onChange={(e) => setRowField(p.item_number, r.id, "size", e.target.value)}
                        onBlur={(e) => saveRowField(p.item_number, r.id, "size", e.target.value)}
                        data-testid={`row-size-${p.item_number}-${ri}`}
                      />
                      {!detailReadOnly && (
                        <button
                          type="button"
                          onClick={() => removeRow(p.item_number, r.id)}
                          data-testid={`quote-remove-row-${p.item_number}-${ri}`}
                          title={t("quote.deleteRow")}
                          className="shrink-0 mt-0.5 h-6 w-6 rounded-sm flex items-center justify-center text-destructive/70 hover:text-destructive hover:bg-destructive/10 transition-colors"
                        >
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  </td>
                ),
                factory_cost: (
                  <td key="factory_cost" className="px-1.5 py-0.5 text-right font-mono text-xs font-bold whitespace-nowrap" data-testid={`row-factory-cost-${p.item_number}-${ri}`}>
                    {fmtRowCost(r)}
                  </td>
                ),
                calc_price: (
                  <td key="calc_price" className="px-1.5 py-0.5 text-right font-mono text-xs font-bold text-brand" data-testid={`row-calc-price-${p.item_number}-${ri}`}>
                    {r.customer_price ? r.customer_price.toFixed(3) : "-"}
                  </td>
                ),
                price: (
                  <td key="price" className="px-0.5 py-0.5">
                    <div className="flex items-center gap-0.5">
                      <Input
                        type="number" step="0.01"
                        value={r.final_price ?? 0}
                        disabled={detailReadOnly}
                        onChange={(e) => setRowField(p.item_number, r.id, "final_price", e.target.value)}
                        onBlur={(e) => saveRowField(p.item_number, r.id, "final_price", parseFloat(e.target.value) || 0)}
                        data-testid={`row-final-price-${p.item_number}-${ri}`}
                        className={`${cellInput} text-right font-mono font-bold`}
                      />
                    </div>
                  </td>
                ),
                qty_per_ctn: (
                  <td key="qty_per_ctn" className="px-1.5 py-0.5 text-right font-mono text-xs font-semibold" data-testid={`row-qty-per-ctn-${p.item_number}-${ri}`}>
                    {r.qty_per_ctn || "-"}
                  </td>
                ),
                qty40: (
                  <td key="qty40" className="px-1.5 py-0.5 text-right font-mono text-xs font-semibold" data-testid={`row-qty40-${p.item_number}-${ri}`}>
                    {r.container_qty_40 || "-"}
                  </td>
                ),
              };

              return (
                <tr key={`${p.item_number}-${r?.id || ri}`}
                  data-row-id={r?.id || ""}
                  data-item-number={p.item_number}
                  className={`${r && r.selected === false ? "opacity-50" : ""} ${dragRow && r && dragRow.rowId === r.id ? "opacity-40" : ""} ${overRow && r && overRow.rowId === r.id && dragRow?.rowId !== r.id ? "bg-brand/10" : ""} ${dragItem === p.item_number ? "opacity-40" : ""} ${overItem === p.item_number ? "bg-brand/10" : ""}`}
                  data-testid={`quote-item-row-${p.item_number}-${ri}`}>
                  <td
                    style={freezeStyle("select")}
                    className={`px-1 py-1 text-center align-middle border-r border-border/40 ${freezeClass("select")}`}
                  >
                    {r ? (
                      <input
                        type="checkbox"
                        disabled={readOnly}
                        checked={r.selected !== false}
                        onChange={(e) => saveRowField(p.item_number, r.id, "selected", e.target.checked)}
                        data-testid={`row-select-${p.item_number}-${ri}`}
                        className="cursor-pointer"
                      />
                    ) : null}
                  </td>
                  {dataCols.map((key) => {
                    if (ITEM_LEVEL.includes(key)) {
                      // 2026-06 · 真正的单元格合并 —— 产品级列用 rowSpan 跨越该
                      // 产品的全部尺寸行（用户要求：图片等单元格要合并）。
                      return ri === 0 ? itemCells[key] : null;
                    }
                    return rowCells[key] || (
                      <td key={key} className="px-1.5 py-0.5 text-xs text-muted-foreground text-center">-</td>
                    );
                  })}
                  <td className="px-0.5 py-0.5" />
                </tr>
              );
            });
          })}
        </tbody>
      </table>
      <ProcurementFilingDialog
        quoteId={detail?.id}
        itemNumber={filingItem}
        open={Boolean(filingItem)}
        onClose={() => setFilingItem(null)}
      />
    </div>
    <FloatingHScrollbar targetRef={scrollBoxRef} />
    </>
  );
}
